import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Leaf, Search, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { getAllRatgeberSlugs, getRatgeberBySlug } from "@/data/ratgeberData";
import logo from "@/assets/logo-new.png";

const Ratgeber = () => {
  const slugs = getAllRatgeberSlugs();
  const ratgeberList = slugs.map(slug => ({
    slug,
    content: getRatgeberBySlug(slug)!
  }));

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Naturheilkunde Ratgeber",
    "description": "Umfassende Ratgeber zu natürlichen Heilmitteln und Hausmitteln für verschiedene Beschwerden.",
    "numberOfItems": ratgeberList.length,
    "itemListElement": ratgeberList.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.content.h1,
      "url": `https://www.love-the-nature.de/ratgeber/${item.slug}`
    }))
  };

  return (
    <>
      <Helmet>
        <title>Naturheilkunde Ratgeber | Hausmittel & Naturmittel | Love The Nature</title>
        <meta name="description" content="Entdecke unsere ausführlichen Ratgeber zu natürlichen Heilmitteln. Von Erkältung bis Schlafprobleme - finde bewährte Hausmittel und Naturmittel für deine Beschwerden." />
        <meta name="keywords" content="Naturheilkunde, Hausmittel, Naturmittel, Heilpflanzen, natürliche Behandlung, Gesundheitsratgeber" />
        <link rel="canonical" href="https://www.love-the-nature.de/ratgeber" />
        <meta property="og:title" content="Naturheilkunde Ratgeber | Love The Nature" />
        <meta property="og:description" content="Ausführliche Ratgeber zu natürlichen Heilmitteln und bewährten Hausmitteln für verschiedene Beschwerden." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.love-the-nature.de/ratgeber" />
        <meta property="og:image" content="https://www.love-the-nature.de/og-image.png" />
        <meta property="og:locale" content="de_DE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Naturheilkunde Ratgeber | Love The Nature" />
        <meta name="twitter:description" content="Ausführliche Ratgeber zu natürlichen Heilmitteln und bewährten Hausmitteln." />
        <meta name="twitter:image" content="https://www.love-the-nature.de/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Love The Nature Logo" className="w-10 h-10 object-contain" />
              <span className="font-serif font-bold text-lg hidden sm:block">Love The Nature</span>
            </Link>
            <Button asChild variant="default" size="sm">
              <a href="https://payhip.com/b/NWTP2" target="_blank" rel="noopener noreferrer">
                E-Book kaufen
              </a>
            </Button>
          </div>
        </header>

        {/* Breadcrumb */}
        <nav className="container mx-auto px-4 py-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">Start</Link>
            </li>
            <ChevronRight className="w-4 h-4" />
            <li className="text-foreground font-medium">Ratgeber</li>
          </ol>
        </nav>

        {/* Hero Section */}
        <section className="py-12 md:py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">{ratgeberList.length} Ratgeber verfügbar</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-4">
              Naturheilkunde Ratgeber
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Entdecke bewährte Hausmittel und natürliche Heilmittel für deine Beschwerden. 
              Unsere ausführlichen Ratgeber zeigen dir, wie du deine Gesundheit natürlich unterstützen kannst.
            </p>
            
            {/* Quick Search */}
            <div className="max-w-md mx-auto">
              <Link 
                to="/#remedy-search"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors group"
              >
                <Search className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                <span className="text-muted-foreground">Nach Beschwerden suchen...</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Ratgeber Grid */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ratgeberList.map(({ slug, content }) => (
                <Link
                  key={slug}
                  to={`/ratgeber/${slug}`}
                  className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Leaf className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {content.h1}
                      </h2>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {content.intro.substring(0, 120)}...
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Leaf className="w-3 h-3" />
                          {content.remedies.length} Naturmittel
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          {content.faqs.length} FAQs
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <span className="text-sm font-medium text-primary">Ratgeber lesen</span>
                    <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-2xl mx-auto p-8 rounded-2xl bg-card border border-primary/20 shadow-lg shadow-primary/5">
              <Leaf className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Alle Naturmittel in einem E-Book
              </h2>
              <p className="text-muted-foreground mb-6">
                Entdecke über 150 Naturmittel mit genauen Dosierungen, Anwendungshinweisen und 
                Warnungen in unserem umfassenden E-Book auf 600+ Seiten.
              </p>
              <Button asChild size="lg" className="gap-2">
                <a href="https://payhip.com/b/NWTP2" target="_blank" rel="noopener noreferrer">
                  E-Book kaufen – 19,99€
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Sofortiger Download • 600+ Seiten • Lebenslanger Zugang
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Ratgeber;